'use client';
import { useEffect, useState } from 'react';
import { getMyBookings, cancelBooking } from 'features/booking/bookingApi';
import Button from 'components/base/Button';
import Badge from 'components/base/Badge';
import { BadgeBg } from 'components/base/Badge';
import { Table, Spinner, Alert, Card, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faMapPin,
  faUserMd,
  faPoundSign,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

interface Booking {
  booking_id: number;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  locum_role: string;
  pay_rate: number;
}

interface StatusInfo {
  bg: BadgeBg;
  label: string;
}

const LocumMyBookingsPage = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelingId, setCancelingId] = useState<number | null>(null);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await getMyBookings();
      setBookings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleCancel = async (bookingId: number) => {
    try {
      setCancelingId(bookingId);

      await cancelBooking(bookingId);

      // update UI instantly
      setBookings(prev =>
        prev.map(b =>
          b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b
        )
      );
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCancelingId(null);
    }
  };

  const getStatusVariant = (status: string): StatusInfo => {
    switch (status) {
      case 'confirmed':
        return { bg: 'success', label: 'Confirmed' };
      case 'cancelled':
        return { bg: 'danger', label: 'Cancelled' };
      case 'completed':
        return { bg: 'info', label: 'Completed' };
      case 'closed':
        return { bg: 'secondary', label: 'Closed' };
      default:
        return { bg: 'secondary', label: status };
    }
  };

  const summary = bookings.reduce(
    (acc, booking) => {
      if (booking.status === 'confirmed') acc.confirmed += 1;
      if (booking.status === 'completed') acc.completed += 1;
      if (booking.status === 'cancelled') acc.cancelled += 1;
      return acc;
    },
    { confirmed: 0, completed: 0, cancelled: 0 }
  );

  return (
    <Container className="container py-2 py-sm-2">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
            Locum Workspace
          </p>
          <h2 className="text-body-emphasis mb-1">My Bookings</h2>
          <p className="mb-0 text-body-tertiary fs-9">
            View and manage all booked shifts.
          </p>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Badge variant="phoenix" bg="success" className="fs-10">
            Confirmed: {summary.confirmed}
          </Badge>
          <Badge variant="phoenix" bg="info" className="fs-10">
            Completed: {summary.completed}
          </Badge>
          <Badge variant="phoenix" bg="danger" className="fs-10">
            Cancelled: {summary.cancelled}
          </Badge>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          {error}
        </Alert>
      )}

      {loading ? (
        <Card className="border border-translucent shadow-sm">
          <Card.Body className="text-center py-6">
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border border-translucent shadow-sm">
          <Card.Header className="bg-body-emphasis d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0 fs-8 text-body-emphasis">
              Booking List
            </Card.Title>
            <small className="text-body-tertiary">
              Total: {bookings.length}
            </small>
          </Card.Header>
          <Card.Body className="p-4">
            {bookings.length === 0 ? (
              <div className="text-center py-6 px-4">
                <p className="text-body-secondary mb-2 fw-semibold">
                  No bookings yet
                </p>
                <p className="text-body-tertiary fs-9 mb-0">
                  Browse available shifts and make your first booking.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="phoenix-table mb-0">
                  <thead className="border-bottom border-translucent">
                    <tr>
                      <th className="fw-bold text-uppercase fs-9">Date</th>
                      <th className="fw-bold text-uppercase fs-9">Time</th>
                      <th className="fw-bold text-uppercase fs-9">Location</th>
                      <th className="fw-bold text-uppercase fs-9">Role</th>
                      <th className="fw-bold text-uppercase fs-9">Pay Rate</th>
                      <th className="fw-bold text-uppercase fs-9">Status</th>
                      <th className="fw-bold text-uppercase fs-9 text-end">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {bookings.map(item => {
                      const statusInfo = getStatusVariant(item.status);
                      return (
                        <tr key={item.booking_id} className="align-middle">
                          <td>
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="me-2 text-primary"
                            />
                            <span className="fw-semibold">
                              {new Date(item.date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit'
                              })}
                            </span>
                          </td>

                          <td>
                            <FontAwesomeIcon
                              icon={faClock}
                              className="me-2 text-info"
                            />
                            <span>
                              {item.start_time.slice(0, 5)} -{' '}
                              {item.end_time.slice(0, 5)}
                            </span>
                          </td>

                          <td>
                            <FontAwesomeIcon
                              icon={faMapPin}
                              className="me-2 text-danger"
                            />
                            <span className="text-body-emphasis">
                              {item.location}
                            </span>
                          </td>

                          <td>
                            <FontAwesomeIcon
                              icon={faUserMd}
                              className="me-2 text-success"
                            />
                            <span className="badge bg-body-secondary text-body-emphasis">
                              {item.locum_role}
                            </span>
                          </td>

                          <td>
                            <FontAwesomeIcon
                              icon={faPoundSign}
                              className="me-2"
                            />
                            <span className="fw-semibold">{item.pay_rate}</span>
                          </td>

                          <td>
                            <Badge
                              bg={statusInfo.bg}
                              variant="phoenix"
                              className="fs-10"
                            >
                              {statusInfo.label}
                            </Badge>
                          </td>

                          <td className="text-end">
                            {item.status === 'confirmed' ? (
                              <Button
                                size="sm"
                                variant="phoenix-danger"
                                onClick={() => handleCancel(item.booking_id)}
                                disabled={cancelingId === item.booking_id}
                                className="px-3"
                              >
                                {cancelingId === item.booking_id ? (
                                  <>
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Cancelling...
                                  </>
                                ) : (
                                  'Cancel'
                                )}
                              </Button>
                            ) : (
                              <span className="text-body-secondary">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default LocumMyBookingsPage;
